"use client";

import { useEffect, useState } from "react";
import { ChevronRight, Heart } from "lucide-react";
import { cn } from "@/lib/utils";
import { normalizeLikesCount } from "@/lib/blog-likes";

type FloatingLikeButtonProps = {
	slug: string;
	type: "BLOG" | "MODULE";
	initialLikesCount: number;
	onLikesCount?: (count: number) => void;
};

type AudioWindow = Window & typeof globalThis & {
	webkitAudioContext?: typeof AudioContext;
};

function playLikeSound() {
	try {
		const audioWindow = window as AudioWindow;
		const AudioContextClass = audioWindow.AudioContext || audioWindow.webkitAudioContext;
		if (!AudioContextClass) return;

		const ctx = new AudioContextClass();
		const now = ctx.currentTime;
		const gain = ctx.createGain();
		const pop = ctx.createOscillator();
		const chime = ctx.createOscillator();

		gain.gain.setValueAtTime(0.0001, now);
		gain.gain.exponentialRampToValueAtTime(0.075, now + 0.012);
		gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);

		pop.type = "sine";
		pop.frequency.setValueAtTime(360, now);
		pop.frequency.exponentialRampToValueAtTime(720, now + 0.055);

		chime.type = "triangle";
		chime.frequency.setValueAtTime(980, now + 0.025);
		chime.frequency.exponentialRampToValueAtTime(1320, now + 0.12);

		pop.connect(gain);
		chime.connect(gain);
		gain.connect(ctx.destination);

		pop.start(now);
		chime.start(now + 0.025);
		pop.stop(now + 0.12);
		chime.stop(now + 0.16);

		window.setTimeout(() => void ctx.close(), 240);
	} catch {
		// Audio feedback is optional.
	}
}

export default function FloatingLikeButton({
	slug,
	type,
	initialLikesCount,
	onLikesCount,
}: FloatingLikeButtonProps) {
	const [likesCount, setLikesCount] = useState(() => normalizeLikesCount(initialLikesCount));
	const [liked, setLiked] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [justChanged, setJustChanged] = useState(false);
	const [showThanks, setShowThanks] = useState(false);
	const [isHiddenOnMobile, setIsHiddenOnMobile] = useState(false);

	useEffect(() => {
		let cancelled = false;

		const loadState = async () => {
			try {
				const endpoint =
					type === "MODULE"
						? `/api/modules/${encodeURIComponent(slug)}/like`
						: `/api/blog-posts/${encodeURIComponent(slug)}/like`;
				const res = await fetch(endpoint, {
					method: "GET",
				});
				const result = (await res.json()) as {
					ok: boolean;
					data?: { liked: boolean; likesCount: number };
				};

				if (!cancelled && result.ok && result.data) {
					setLiked(result.data.liked);
					const nextCount = normalizeLikesCount(result.data.likesCount);
					setLikesCount(nextCount);
					onLikesCount?.(nextCount);
				}
			} catch {
				// Liking is progressive enhancement; content remains readable.
			}
		};

		void loadState();

		return () => {
			cancelled = true;
		};
	}, [slug, type]);

	const toggleLike = async () => {
		if (isLoading) return;

		const previousLiked = liked;
		const previousCount = likesCount;
		const nextLiked = !previousLiked;
		const nextCount = normalizeLikesCount(previousCount + (nextLiked ? 1 : -1));

		setLiked(nextLiked);
		setLikesCount(nextCount);
		onLikesCount?.(nextCount);
		setJustChanged(true);
		setIsLoading(true);
		window.setTimeout(() => setJustChanged(false), 620);
		if (nextLiked) {
			playLikeSound();
			setShowThanks(true);
			window.setTimeout(() => setShowThanks(false), 1400);
		} else {
			setShowThanks(false);
		}

		try {
			const endpoint =
				type === "MODULE"
					? `/api/modules/${encodeURIComponent(slug)}/like`
					: `/api/blog-posts/${encodeURIComponent(slug)}/like`;
			const res = await fetch(endpoint, {
				method: "POST",
			});
			const result = (await res.json()) as {
				ok: boolean;
				data?: { liked: boolean; likesCount: number };
			};

			if (!res.ok || !result.ok || !result.data) {
				throw new Error("Failed to update like.");
			}

			setLiked(result.data.liked);
			const serverCount = normalizeLikesCount(result.data.likesCount);
			setLikesCount(serverCount);
			onLikesCount?.(serverCount);
		} catch {
			setLiked(previousLiked);
			setLikesCount(previousCount);
			onLikesCount?.(previousCount);
		} finally {
			setIsLoading(false);
		}
	};

	const formattedCount = new Intl.NumberFormat("en-US", {
		notation: likesCount >= 10000 ? "compact" : "standard",
		maximumFractionDigits: 1,
	}).format(likesCount);

	return (
		<>
			{isHiddenOnMobile && (
				<button
					type="button"
					onClick={() => setIsHiddenOnMobile(false)}
					className="fixed right-3 top-[calc(50%+1.5rem)] z-50 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-white/20 bg-black/60 text-white/65 shadow-[0_14px_34px_rgba(0,0,0,0.42),inset_0_0_18px_rgba(255,255,255,0.04)] backdrop-blur transition hover:scale-105 hover:border-white/35 hover:bg-white/[0.055] hover:text-white active:scale-95 sm:hidden"
					aria-label="Show like button"
				>
					<Heart className={cn("h-3.5 w-3.5", liked && "fill-zinc-100 text-zinc-100")} />
				</button>
			)}

			{!isHiddenOnMobile && (
				<button
					type="button"
					onClick={() => setIsHiddenOnMobile(true)}
					className="fixed right-[7.1rem] top-[calc(50%+1.5rem)] z-50 flex h-9 w-9 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-white/20 bg-black/60 text-white/65 shadow-[0_14px_34px_rgba(0,0,0,0.42),inset_0_0_18px_rgba(255,255,255,0.04)] backdrop-blur transition hover:scale-105 hover:border-white/35 hover:bg-white/[0.055] hover:text-white active:scale-95 sm:hidden"
					aria-label="Hide like button"
				>
					<ChevronRight className="h-3.5 w-3.5" />
				</button>
			)}

			<button
				type="button"
				onClick={toggleLike}
				disabled={isLoading}
				aria-pressed={liked}
				aria-label={liked ? "Unlike this content" : "Like this content"}
				className={cn(
					"group fixed right-1 top-1/2 z-40 h-36 w-28 -translate-y-1/2 cursor-pointer outline-none transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] hover:-translate-y-[calc(50%+4px)] active:-translate-y-[calc(50%-1px)] active:cursor-pointer sm:right-5 sm:h-44 sm:w-36 lg:right-8",
					justChanged && "scale-[1.07]",
					isLoading && "cursor-wait",
					isHiddenOnMobile && "pointer-events-none translate-x-[120%] opacity-0 sm:pointer-events-auto sm:translate-x-0 sm:opacity-100",
				)}
			>
				<span
					className={cn(
						"pointer-events-none absolute bottom-[6.6rem] left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-white/12 bg-black/60 px-3 py-1.5 text-xs font-medium text-zinc-100 opacity-0 shadow-[0_12px_30px_rgba(0,0,0,0.35)] backdrop-blur transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] sm:bottom-[8.7rem]",
						showThanks && "-translate-y-2 opacity-100",
					)}
					aria-live="polite"
				>
					Thank you
				</span>
			<span
				className={cn(
					"absolute bottom-0 left-1/2 flex h-24 w-24 -translate-x-1/2 items-center justify-center overflow-hidden rounded-full border bg-black/35 shadow-[0_24px_56px_rgba(0,0,0,0.45)] backdrop-blur-sm transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.035] group-active:scale-90 sm:h-32 sm:w-32",
					liked
						? "border-white/35 bg-white/[0.055] shadow-[0_24px_56px_rgba(0,0,0,0.5),inset_0_0_28px_rgba(255,255,255,0.06)]"
						: "border-white/20 group-hover:border-white/32 group-hover:bg-white/[0.03]",
					justChanged && "scale-[1.08] rounded-[2.35rem] border-white/45 bg-white/[0.07] sm:rounded-[3rem]",
					isLoading && "scale-[0.9] rounded-[2rem] border-white/40 bg-white/[0.07] shadow-[0_18px_42px_rgba(0,0,0,0.5),inset_0_0_36px_rgba(255,255,255,0.075)] sm:rounded-[2.6rem]",
				)}
			>
				<span
					aria-hidden="true"
					className={cn(
						"absolute h-0 w-0 rounded-full bg-white/[0.075] opacity-0 transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)]",
						justChanged && "h-56 w-56 opacity-100 sm:h-72 sm:w-72",
						isLoading && "h-20 w-20 opacity-80 sm:h-28 sm:w-28",
					)}
				/>
				<span
					aria-hidden="true"
					className={cn(
						"absolute h-0 w-0 rounded-full border border-white/0 opacity-0 transition-all duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
						justChanged && "h-72 w-72 border-white/10 opacity-100 sm:h-96 sm:w-96",
					)}
				/>
				<span
					aria-hidden="true"
					className={cn(
						"absolute inset-5 rounded-full border border-white/0 opacity-0 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] sm:inset-7",
						liked && "inset-3 border-white/10 opacity-100 sm:inset-5",
						isLoading && "inset-1 rounded-[1.65rem] border-white/18 opacity-100 sm:rounded-[2.2rem]",
					)}
				/>
				<span
					aria-hidden="true"
					className={cn(
						"absolute h-10 w-10 rounded-full bg-white/0 blur-xl transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] sm:h-14 sm:w-14",
						liked && "h-16 w-16 bg-white/10 sm:h-20 sm:w-20",
						isLoading && "h-20 w-20 bg-white/12 sm:h-24 sm:w-24",
					)}
				/>
				<Heart
					className={cn(
						"relative h-12 w-12 drop-shadow-[0_10px_18px_rgba(0,0,0,0.75)] transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.08] group-active:scale-75 sm:h-16 sm:w-16",
						liked
							? "scale-110 fill-zinc-100 text-zinc-100"
							: "fill-zinc-700/35 text-white/25 group-hover:fill-zinc-600/45 group-hover:text-white/40",
						justChanged && "scale-[1.42] rotate-[-7deg] drop-shadow-[0_16px_28px_rgba(255,255,255,0.13)]",
						isLoading && "scale-75 fill-zinc-200/60 text-zinc-200/60",
					)}
				/>
			</span>
			<span className="sr-only">{liked ? "Liked" : "Not liked"}, {formattedCount} likes</span>
			</button>
		</>
	);
}
